import { useCallback, useMemo } from 'react';
import { IGroup } from 'types';

import {
  usePaginatedGroupsQuery,
  GroupEdge,
  PaginatedGroupsQueryVariables,
  PageInfo,
} from 'core/graphql';

import { isPermissionError } from 'core/utils/isPermissionError';

import { toIGroupArray } from './transform';
import { useGroupPermissions } from './useGroupPermissions';

type GroupsHookType = {
  data: IGroup[];
  loading?: boolean;
  error?: string;
  /** Отказ по правам (а не сбой) — список говорит «Нет доступа». */
  forbidden: boolean;
  totalCount?: number | null;
  pageInfo?: Pick<
    PageInfo,
    'startCursor' | 'endCursor' | 'hasNextPage' | 'hasPreviousPage'
  >;
  operations: {
    fetchMoreData: (vars: Partial<PaginatedGroupsQueryVariables>) => void;
  };
};

const useGetGroupsFromApi = () => {
  // `price` / `currency` — только `GROUP_STAFF_FIELDS_ROLES`: методисту их
  // поля не запрашиваются (иначе — отказ поля на весь список).
  const { canReadGroupStaffFields } = useGroupPermissions();
  const { data, error, loading, fetchMore } = usePaginatedGroupsQuery({
    fetchPolicy: 'cache-first',
    variables: {
      first: 10,
      withStaffFields: canReadGroupStaffFields,
    },
    notifyOnNetworkStatusChange: true,
  });

  // Memoized + O(n) push (spec 0008 round 2): the old spread-in-reduce was
  // O(n²) and re-ran on every render.
  const result = useMemo<IGroup[]>(() => {
    const edges = data?.allGroups?.edges;
    if (!edges) {
      return [];
    }
    const groups: IGroup[] = [];
    edges.forEach((edge) => {
      if (!edge) return;
      const groupArr = toIGroupArray(edge as GroupEdge);
      if (groupArr) groups.push(groupArr);
    });
    return groups;
  }, [data?.allGroups?.edges]);

  return {
    fetchMore,
    result,
    error,
    loading,
    pageInfo: data?.allGroups?.pageInfo,
    totalCount: data?.allGroups?.totalCount,
  };
};

export const useGroups = (): GroupsHookType => {
  const {
    result,
    error,
    loading,
    totalCount,
    fetchMore,
    pageInfo,
  } = useGetGroupsFromApi();

  const fetchMoreData = useCallback(
    ({ query, after }: Partial<PaginatedGroupsQueryVariables>) => {
      // Ошибка догрузки уже лежит в `error` запроса — обещание гасим, иначе
      // отказ всплывал «Unhandled Rejection» поверх страницы.
      fetchMore({
        variables: {
          query,
          after,
        },
      }).catch(() => undefined);
    },
    [fetchMore],
  );

  return {
    data: result || [],
    loading,
    pageInfo,
    error: error?.message,
    forbidden: isPermissionError(error),
    totalCount,
    operations: {
      fetchMoreData,
    },
  };
};
